import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Video, VideoOff, Mic, MicOff, PhoneOff, Maximize, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const JITSI_DOMAIN = "meet.jit.si";

export default function CRMVideoCall() {
  const { appointmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);
  const [participants, setParticipants] = useState(0);

  useEffect(() => {
    loadAppointment();
    return () => { if (apiRef.current) apiRef.current.dispose(); };
  }, [appointmentId]);

  const loadAppointment = async () => {
    try {
      const headers = { Authorization: `Bearer ${user.token}` };
      const res = await axios.get(`${API}/appointments/${appointmentId}`, { headers });
      setAppointment(res.data);
    } catch { navigate("/app/appointments"); }
    setLoading(false);
  };

  const startCall = () => {
    if (!appointment?.video_room || !containerRef.current) return;

    const scriptId = "jitsi-external-api";
    const loadScript = () =>
      new Promise((resolve, reject) => {
        if (window.JitsiMeetExternalAPI) return resolve();
        if (document.getElementById(scriptId)) {
          const check = setInterval(() => {
            if (window.JitsiMeetExternalAPI) { clearInterval(check); resolve(); }
          }, 100);
          return;
        }
        const script = document.createElement("script");
        script.id = scriptId;
        script.src = `https://${JITSI_DOMAIN}/external_api.js`;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });

    loadScript().then(() => {
      if (apiRef.current) apiRef.current.dispose();

      apiRef.current = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
        roomName: appointment.video_room,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: {
          displayName: user?.name || "Berater",
          email: user?.email || "",
        },
        configOverwrite: {
          startWithAudioMuted: false,
          startWithVideoMuted: false,
          prejoinPageEnabled: false,
          disableDeepLinking: true,
          enableWelcomePage: false,
          defaultLanguage: "de",
          toolbarButtons: [
            'camera', 'chat', 'closedcaptions', 'desktop', 'fullscreen',
            'hangup', 'microphone', 'participants-pane', 'raisehand',
            'settings', 'tileview', 'toggle-camera',
          ],
        },
        interfaceConfigOverwrite: {
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          SHOW_BRAND_WATERMARK: false,
          DEFAULT_BACKGROUND: '#F8FAFC',
          TOOLBAR_ALWAYS_VISIBLE: true,
          DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
          MOBILE_APP_PROMO: false,
        },
      });

      apiRef.current.addListener("videoConferenceJoined", () => setJoined(true));
      apiRef.current.addListener("videoConferenceLeft", () => { setJoined(false); });
      apiRef.current.addListener("participantJoined", () => setParticipants(p => p + 1));
      apiRef.current.addListener("participantLeft", () => setParticipants(p => Math.max(0, p - 1)));
      apiRef.current.addListener("readyToClose", () => { setJoined(false); });
    });
  };

  const endCall = () => {
    if (apiRef.current) {
      apiRef.current.executeCommand("hangup");
      apiRef.current.dispose();
      apiRef.current = null;
    }
    setJoined(false);
  };

  const copyJoinLink = () => {
    const link = `${window.location.origin}/video/${appointment.video_room}`;
    navigator.clipboard.writeText(link);
  };

  const formatDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-cyan-700 border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="crm-video-call" className="h-[calc(100vh-7rem)]">
      {!joined ? (
        /* Pre-call screen */
        <div className="flex flex-col items-center justify-center h-full">
          <div className="bg-white rounded-2xl border border-slate-200/60 p-8 max-w-lg w-full text-center">
            <div className="h-16 w-16 bg-cyan-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Video className="h-8 w-8 text-cyan-700" />
            </div>
            <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-2">
              Video-Beratung starten
            </h2>
            {appointment && (
              <div className="bg-slate-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm font-medium text-slate-700">{appointment.name}</p>
                <p className="text-xs text-slate-500 mt-1">{formatDate(appointment.date)} — {appointment.time_slot} Uhr</p>
                <p className="text-xs text-slate-500">{appointment.email} | {appointment.phone}</p>
                {appointment.pflegegrad && <p className="text-xs text-slate-500">Pflegegrad {appointment.pflegegrad}</p>}
              </div>
            )}

            <Button
              data-testid="start-video-btn"
              onClick={startCall}
              className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl h-12 text-base font-semibold mb-3"
            >
              <Video className="h-5 w-5 mr-2" /> Video-Beratung starten
            </Button>

            <Button
              variant="outline"
              onClick={copyJoinLink}
              className="w-full rounded-xl h-10 text-sm mb-4"
              data-testid="copy-link-btn"
            >
              Teilnahme-Link kopieren
            </Button>

            <p className="text-xs text-slate-400">
              Kunden-Link: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">{window.location.origin}/video/{appointment?.video_room}</code>
            </p>

            <button onClick={() => navigate(-1)} className="mt-6 text-sm text-slate-500 hover:text-cyan-700 transition-colors">
              <ArrowLeft className="h-3.5 w-3.5 inline mr-1" /> Zurück
            </button>
          </div>
        </div>
      ) : (
        /* Active call */
        <div className="h-full flex flex-col">
          {/* Call header */}
          <div className="bg-white border-b border-slate-200 px-4 py-2 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="h-2.5 w-2.5 bg-green-500 rounded-full animate-pulse" />
              <span className="text-sm font-medium text-slate-700">
                Beratung mit {appointment?.name}
              </span>
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Users className="h-3 w-3" /> {participants + 1} Teilnehmer
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={copyJoinLink}>
                Link kopieren
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-lg" onClick={endCall} data-testid="end-call-btn">
                <PhoneOff className="h-4 w-4 mr-1" /> Beenden
              </Button>
            </div>
          </div>
          {/* Jitsi container */}
          <div ref={containerRef} className="flex-1 bg-slate-900" data-testid="jitsi-container" />
        </div>
      )}
    </div>
  );
}
